'use client';

import { useTranslations } from 'next-intl';
import type { ListOfLocationsInCityResolved } from './types';
import { Icon } from '@/components/Icon';
import { Link } from '@/components/Link';

// Card grid of the city's Standorte (10.03.03.26 List Of Locations In City).
// No widget title — editors place a Title Widget before it. The appointment
// target is the resolver's provisional `#terminvereinbarung` anchor: per-Standort
// booking disambiguation is a separate, unscoped Book Appointment update.
const FALLBACK_APPOINTMENT_HREF = '#terminvereinbarung';

export function ListOfLocationsInCityComponent({
  resolved,
}: {
  resolved: ListOfLocationsInCityResolved;
}) {
  const t = useTranslations('listOfLocationsInCity');
  const locations = resolved.locations ?? [];

  if (locations.length === 0) {
    return null;
  }

  return (
    <section className="pt-4">
      <ul className="grid list-none grid-cols-1 gap-4 md:grid-cols-2">
        {locations.map((location) => (
          <li
            key={location.slug}
            className="bg-secondary-lighter relative my-0 flex flex-col items-center gap-3 rounded-2xl px-4 py-8 text-center"
          >
            <div className="flex flex-col items-center gap-1">
              <p className="h3 my-0 text-base font-medium">
                {t('cardTitle', { name: location.name })}
              </p>
              {location.address && (
                <p className="my-0">
                  {[
                    location.address.street,
                    [location.address.postcode, location.address.city]
                      .filter(Boolean)
                      .join(' '),
                  ]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
            </div>
            <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2">
              {/* Stretched link: the ::after overlay makes the whole tile
                  navigate to the Standort; phone and appointment stay above
                  it via their own positioned boxes. */}
              <Link
                href={location.href}
                className="text-primary flex items-center gap-1 after:absolute after:inset-0 hover:underline"
              >
                <Icon
                  src="/icons/svg/local-pin.svg"
                  size={20}
                  className="shrink-0"
                />
                {resolved.locationLinkLabel || t('locationLinkLabel')}
              </Link>
              {location.phone && (
                <a
                  href={`tel:${location.phone.replace(/[\s/]/g, '')}`}
                  className="text-primary relative flex items-center gap-1 hover:underline"
                >
                  <Icon
                    src="/icons/svg/phone-filled.svg"
                    size={20}
                    className="shrink-0"
                  />
                  {location.matelsoPhoneLabel || location.phone}
                </a>
              )}
            </div>
            <a
              href={location.appointmentHref ?? FALLBACK_APPOINTMENT_HREF}
              className="text-primary relative flex items-center gap-1 hover:underline"
            >
              <Icon
                src="/icons/svg/calendar.svg"
                size={20}
                className="shrink-0"
              />
              {resolved.appointmentCtaLabel || t('appointmentCtaLabel')}
            </a>
          </li>
        ))}
      </ul>
    </section>
  );
}
